import React, { useState, useEffect } from 'react';
import { useCreate, useList } from "@refinedev/core";
import { useNavigate } from 'react-router-dom';

interface IMDFFile {
  id: string;
  originalName: string;
  version?: string;
  category?: string;
  status: 'pending' | 'validating' | 'valid' | 'invalid';
}

interface CreateProjectProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateProject: React.FC<CreateProjectProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedImdfIds, setSelectedImdfIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // 取得所有 IMDF 檔案
  const { data: imdfData, isLoading: imdfLoading } = useList<IMDFFile>({
    resource: "imdf",
    config: {
      pagination: {
        mode: "off"
      }
    }
  });

  // 建立專案的 API 調用
  const { mutate: createProject, isLoading: isCreating } = useCreate();

  // 篩選 IMDF 檔案
  const filteredFiles = imdfData?.data.filter(file => 
    file.originalName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (file.category && file.category.toLowerCase().includes(searchQuery.toLowerCase())) ||
    (file.version && file.version.toLowerCase().includes(searchQuery.toLowerCase()))
  ) || [];

  // 切換選擇 IMDF 檔案
  const toggleImdfSelection = (id: string) => {
    setSelectedImdfIds(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id) 
        : [...prev, id]
    );
  };

  // 建立專案
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      alert('請輸入專案名稱');
      return;
    }

    createProject(
      {
        resource: "projects",
        values: {
          name,
          description,
          imdfSet: selectedImdfIds
        },
      },
      {
        onSuccess: () => {
          onClose();
          // 關閉對話框後留在當前頁面（專案列表）
          // 如果需要刷新列表，可以考慮觸發重新獲取資料
          window.location.reload(); // 簡單的方式是重新載入頁面來刷新數據
        },
        onError: (error) => {
          console.error('創建專案失敗', error);
          alert('創建專案失敗，請稍後再試');
        }
      }
    );
  };

  // 如果對話框關閉，重置表單
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setDescription('');
      setSelectedImdfIds([]);
      setSearchQuery('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* 標題列 */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">建立新專案</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 表單內容 */}
        <div className="flex-grow overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 專案名稱 */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                專案名稱 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="輸入專案名稱"
                required
              />
            </div>

            {/* 專案描述 */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                專案描述
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="輸入專案描述（選填）"
              />
            </div>

            {/* IMDF 檔案選擇 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                選擇 IMDF 檔案
              </label>
              <div className="space-y-3">
                {/* 搜尋框 */}
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="搜尋 IMDF 檔案..."
                  />
                  <div className="absolute left-3 top-2.5 text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>

                {/* 檔案列表 */}
                <div className="border border-gray-300 rounded-md overflow-hidden">
                  {imdfLoading ? (
                    <div className="p-4 text-center text-gray-500">
                      載入 IMDF 檔案中...
                    </div>
                  ) : filteredFiles.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      {searchQuery ? '找不到符合的 IMDF 檔案' : '尚無 IMDF 檔案可選擇'}
                    </div>
                  ) : (
                    <div className="max-h-60 overflow-y-auto">
                      {filteredFiles.map((file) => (
                        <div
                          key={file.id}
                          className={`px-4 py-3 flex items-center border-b border-gray-200 last:border-b-0 hover:bg-gray-50 cursor-pointer ${
                            selectedImdfIds.includes(file.id) ? 'bg-blue-50' : ''
                          }`}
                          onClick={() => toggleImdfSelection(file.id)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedImdfIds.includes(file.id)}
                            onChange={() => {}}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-3"
                          />
                          <div className="flex-grow">
                            <div className="font-medium text-gray-800">{file.originalName}</div>
                            <div className="flex space-x-2 text-xs text-gray-500 mt-1">
                              {file.version && <span>版本: {file.version}</span>}
                              {file.category && <span>分類: {file.category}</span>}
                              <span className={`px-2 py-0.5 rounded-full ${
                                file.status === 'valid' ? 'bg-green-100 text-green-800' :
                                file.status === 'invalid' ? 'bg-red-100 text-red-800' :
                                file.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {file.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 已選擇的檔案數量 */}
                <div className="text-sm text-gray-600">
                  已選擇 {selectedImdfIds.length} 個檔案
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* 表單底部按鈕 */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isCreating || !name.trim()}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              isCreating || !name.trim() 
                ? 'bg-blue-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isCreating ? '建立中...' : '建立專案'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateProject;